<template>
    <AppLoader :force-loading="loadingPage" default-height="100vh" background="#f9fafb">
        <div class="container mx-auto p-6 space-y-8">
            <!-- 1. Objective -->
            <section class="bg-white shadow rounded-lg p-6">
                <h2 class="text-xl font-semibold mb-4"><span class="font-semibold mr-2">1.</span>Objective / Goal</h2>
                <p class="text-gray-600 mb-4">Define why is this transform needed, what problem it solves, and its expected outcome.</p>
                <textarea
                    id="transformObjective"
                    v-model="transformObjective"
                    rows="3"
                    class="shadow-xs block w-full sm:text-sm border border-gray-300 rounded-md"
                    :placeholder="`Why create this transform ...`"></textarea>
            </section>

            <!-- 2. Input/Output Topics -->
            <section class="bg-white shadow rounded-lg p-6 transition-opacity">
                <h2 class="text-xl font-semibold mb-4"><span class="font-semibold mr-2">2.</span>Input / Output Topics</h2>
                <p class="text-gray-600 mb-4">
                    Define patterns and output topics via the
                    <a href="/topics" target="_blank" class="text-green-400 underline">topics page</a>.
                </p>
                <div class="grid grid-cols-1 md:[grid-template-columns:1fr_1fr_auto] gap-4 w-full">
                    <div class="w-full">
                        <label class="block text-sm font-medium text-gray-700"
                            >Input Topic Pattern
                        </label>
                        <input
                            v-model="pattern"
                            :disabled="transform?.transform_type == TRANSFORM_TYPE_ENUMS.sql_join || transform?.transform_type == TRANSFORM_TYPE_ENUMS.enrich"
                            type="text"
                            placeholder="e.g. .*\\.test\\.some-topic-prefix.*"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-cyan-200 focus:ring-opacity-50" />
                        
                        <!-- ############################################################################## -->
                        <!-- Join specific stuff -->
                        <!-- ############################################################################## -->
                        <div v-if="transform?.transform_type === TRANSFORM_TYPE_ENUMS.sql_join" class="bg-white shadow rounded-lg p-6 transition-opacity">
                            <h2 class="text-xl font-semibold mb-4"><span class="font-semibold mr-2">2.1.</span>Input Tables</h2>
                            <div class="bg-gray-50 border-l-4 border-gray-200 p-4 mb-4 space-y-4">
                                <div v-for="(pattern, index) in inputPatterns" :key="index" class="flex items-center space-x-4">
                                    <input
                                        v-model="inputPatterns[index]"
                                        @input="updateInputPattern(index, $event.target.value)"
                                        type="text"
                                        class="border border-gray-300 rounded-md p-2 flex-grow"
                                        placeholder="Enter topic pattern"
                                    />
                                    <button
                                        @click="removeInputPattern(index)"
                                        :disabled="implementationCurrentVersion?.version_id == null || loadingCheckErrors"
                                        class="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-red-700 hover:bg-red-700 text-white focus:outline-none focus:ring transition disabled:opacity-50 w-[8rem]">
                                        <PlusIconIcon v-if="loadingCheckErrors" class="h-5 w-5 animate-spin mr-2" />
                                        <CogIcon class="w-5 h-5 mr-2" />
                                        Remove
                                    </button>
                                </div>
                                <button
                                        @click="addInputPattern(index)"
                                        :disabled="implementationCurrentVersion?.version_id == null || loadingCheckErrors"
                                        class="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-green-500 hover:bg-green-400 text-white focus:outline-none focus:ring transition disabled:opacity-50 w-[8rem]">
                                        <PlusIconIcon v-if="loadingCheckErrors" class="h-5 w-5 animate-spin mr-2" />
                                        <CogIcon class="w-5 h-5 mr-2" />
                                        Add
                                </button>
                            </div>
                        </div>
                        
                        <!-- ############################################################################## -->
                        <!-- Enrich specific stuff -->
                        <!-- ############################################################################## -->
                        <div>
                            <label v-if="transform?.transform_type == TRANSFORM_TYPE_ENUMS.enrich" class="block text-sm font-medium text-gray-700"
                                >Input Topic Pattern for Main Table (stream to be enriched)</label>
                            <input
                                v-if="transform?.transform_type == TRANSFORM_TYPE_ENUMS.enrich"
                                v-model="patternMainTable"
                                type="text"
                                placeholder="e.g. .*.database.other-topic-prefix.*"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-cyan-200 focus:ring-opacity-50" />
                            <label v-if="transform?.transform_type == TRANSFORM_TYPE_ENUMS.enrich" class="block text-sm font-medium text-gray-700"
                                >Input Topic Pattern for Lookup Table (stream containing lookup values for enrichment)</label>
                            <input
                                v-if="transform?.transform_type == TRANSFORM_TYPE_ENUMS.enrich"
                                v-model="patternLookupTable"
                                type="text"
                                placeholder="e.g. .*.database.other-topic-prefix.*"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-cyan-200 focus:ring-opacity-50" />
                        </div>
                    </div>
                    <div class="w-full">
                        <label class="block text-sm font-medium text-gray-700">Output Topic Replacement (backreferences supported)</label>
                        <input
                            v-model="outputTopic"
                            type="text"
                            placeholder="e.g. test.merged_orders"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-cyan-200 focus:ring-opacity-50" />
                    </div>
                    <div class="flex items-end space-x-3">
                        <button
                            @click="callSetInOutTopics"
                            :disabled="loadingSetInOutTopics"
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-cyan-950 hover:bg-cyan-900 text-white focus:outline-none focus:ring transition disabled:opacity-50">
                            <ArrowPathIcon v-if="loadingSetInOutTopics" class="h-5 w-5 animate-spin mr-2" />
                            <CogIcon class="w-5 h-5 mr-2" />
                            Save Topics Pattern / Replacement
                        </button>
                    </div>
                </div>
                <div v-if="setInOutResult" class="rounded-md bg-green-50 p-4 mt-4">
                    <div class="flex">
                        <div class="shrink-0">
                            <CheckCircleIcon class="w-5 h-5 text-green-400" aria-hidden="true" />
                        </div>
                        <p class="ml-3 text-sm text-green-700">{{ setInOutResult }}</p>
                    </div>
                </div>
                <div v-if="setInOutError" class="mt-2">
                    <ErrorMessage :error="setInOutError" :to-sentry="true" context="transform-impl-set-topics" />
                </div>

                <!-- Topics Search & Table -->
                <div v-if="fetchInOutError" class="mt-2">
                    <ErrorMessage :error="fetchInOutError" :to-sentry="true" context="transform-impl-fetch-topics" />
                </div>
                <div class="table-page-container mt-10 mb-5">
                    <div class="table-page-body">
                        <div>
                            <!-- TODO: transforms/topics-for-pattern API does not support pagination -->
                            <div class="overflow-x-auto overflow-y-auto max-h-96 bg-white rounded-lg shadow-md">
                                <section class="paginated-section">
                                    <AppLoader
                                        :force-loading="topicTableDataLoading"
                                        default-height="30vh"
                                        background="#f9fafb"
                                        :default-width="'100%'"
                                        :default-class="'shadow-md rounded-md'">
                                        <table class="min-w-full divide-y divide-gray-300" aria-label="topics table">
                                            <thead class="bg-gray-50">
                                                <tr>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                        Input Topic
                                                    </th>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                        Expected Output Topic Name
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody class="bg-white divide-y divide-gray-200">
                                                <tr
                                                    v-for="inOutTopic in inOutTopics"
                                                    :key="inOutTopic[0]"
                                                    class="duration-300 ease-in-out hover:bg-gray-50">
                                                    <td class="px-6 py-4 whitespace-nowrap cursor-pointer font-medium text-green-500 no-underline">
                                                        <div class="flex flex-col items-start gap-1">
                                                            <div class="my-auto whitespace-normal break-words hover:text-green-500">
                                                                <a :href="`/topics/${inOutTopic[0]}`" target="_blank">
                                                                    {{ inOutTopic[0].replace(/(source_|transform_)[^.]+\./, '') }}
                                                                </a>
                                                            </div>
                                                            <small class="font-regular text-gray-400">ID: {{ inOutTopic[0] }}</small>
                                                        </div>
                                                    </td>

                                                    <td class="px-6 py-4 whitespace-nowrap font-medium no-underline">
                                                        <div class="flex flex-col items-start gap-1">
                                                            <div class="my-auto whitespace-normal break-words text-gray-800">
                                                                {{ inOutTopic[1].replace(/(source_|transform_)[^.]+\./, '') }}
                                                            </div>
                                                            <small class="font-regular text-gray-500">ID: {{ inOutTopic[1] }}</small>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr v-if="!inOutTopics.length" class="text-center">
                                                    <td colspan="2" class="px-6 py-4 text-gray-500">No topics found for the given pattern.</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </AppLoader>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!--# Map Filter ############################################################################################### -->

            <section
                v-if="
                    [
                        TRANSFORM_TYPE_ENUMS.map_filter,
                        TRANSFORM_TYPE_ENUMS.enrich_async,
                        TRANSFORM_TYPE_ENUMS.fan_out,
                        TRANSFORM_TYPE_ENUMS.un_nesting
                    ].includes(transform?.transform_type)
                "
                class="bg-white shadow rounded-lg p-6 transition-opacity">
                <h2 class="text-xl font-semibold mb-4"><span class="font-semibold mr-2">3.</span>Transform Logic</h2>
                <div class="bg-gray-50 border-l-4 border-gray-200 p-4 mb-4 space-y-4">
                    <p class="text-gray-600">
                        Use
                        <button
                            aria-label="button"
                            @click.prevent="openEditor('VALUE_TRANSFORM')"
                            class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                            <CodeBracketIcon class="mr-1 h-4 w-4" /> valueTransform{{ getFileExtension() }}
                        </button>
                        to transform the record value. This file must define the function
                        <code class="bg-gray-100 px-1 rounded text-sm font-mono">_streamkap_transform(valueObject, keyObject, topic)</code> which
                        takes a single record value as a plain {{
                            implementationCurrentVersion?.implementation.language?.toUpperCase() === TRANSFORM_LANGUAGE_ENUM.PYTHON
                                ? 'Python dictionary'
                                : 'Javascript object'
                        }} and returns the transformed value.
                    </p>
                    <p class="text-gray-600">
                        If needed, use
                        <button
                            aria-label="button"
                            @click.prevent="openEditor('KEY_TRANSFORM')"
                            class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                            <CodeBracketIcon class="mr-1 h-4 w-4" /> keyTransform{{ getFileExtension() }}
                        </button>
                        to transform the record key. This file must define the function
                        <code class="bg-gray-100 px-1 rounded text-sm font-mono">_streamkap_transform_key(valueObject, keyObject, topic)</code>.
                    </p>
                    <p class="text-gray-600">
                        If needed, use
                        <button
                            aria-label="button"
                            @click.prevent="openEditor('TOPIC_TRANSFORM')"
                            class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                            <CodeBracketIcon class="mr-1 h-4 w-4" /> topicTransform{{ getFileExtension() }}
                        </button>
                        to transform the record topic. This file must define the function
                        <code class="bg-gray-100 px-1 rounded text-sm font-mono">_streamkap_transform_topic(valueObject, keyObject, topic)</code>.
                    </p>

                    <!-- Inline Monaco Editor -->
                    <div
                        v-if="editorVisible && ['VALUE_TRANSFORM', 'KEY_TRANSFORM', 'TOPIC_TRANSFORM'].includes(editorPath)"
                        class="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                        <div class="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                            <h3 class="text-lg font-medium">{{ editorPath }}</h3>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="saveCurrentImplementation"
                                    :disabled="!hasUnsavedEditorChanges || savingEditor"
                                    aria-label="Save"
                                    class="inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring transition"
                                    :class="savingEditor
                                        ? 'bg-green-500 hover:bg-green-400 text-white'
                                        : hasUnsavedEditorChanges
                                        ? 'bg-green-500 hover:bg-green-400 text-white'
                                        : 'bg-gray-300 text-gray-600 cursor-default'">
                                    <ArrowPathIcon v-if="savingEditor" class="animate-spin w-5 h-5 mr-1" />
                                    <CheckIcon v-else class="w-5 h-5 mr-1" />
                                    <span v-if="savingEditor">Saving...</span>
                                    <span v-else-if="hasUnsavedEditorChanges">Save</span>
                                    <span v-else>Saved</span>
                                </button>
                                <button @click="closeEditor" aria-label="Close" class="text-gray-500 hover:text-gray-800 focus:outline-none">
                                    <XMarkIcon class="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <AppLoader v-if="editorLoading" :force-loading="editorLoading" class="h-168" />
                        <VueMonacoEditor
                            v-else
                            v-model:value="editorContent"
                            :language="editorLanguage"
                            :path="editorPath"
                            theme="vs"
                            style="height: undefined !important; width: auto !important"
                            class="h-168"
                            @change="onEditorChange"
                            @mount="onEditorMount" />
                    </div>
                </div>
            </section>

            <!--################################################################################################ -->
            <!--################################################################################################ -->
            <!--# Enrich ####################################################################################### -->
            <!--################################################################################################ -->
            <!--################################################################################################ -->

            <section v-if="transform?.transform_type == TRANSFORM_TYPE_ENUMS.enrich" class="bg-white shadow rounded-lg p-6 transition-opacity">
                <h2 class="text-xl font-semibold mb-4"><span class="font-semibold mr-2">3.</span>Transform Logic</h2>
                <div class="bg-gray-50 border-l-4 border-gray-200 p-4 mb-4 space-y-4">
                    <p class="text-gray-600">
                        <code>CREATE TABLE</code> definitions, only fields used in the join are needed, the rest can be passed through automatically.
                    </p>
                    <p class="text-gray-600">
                        Main Table
                        <button aria-label="button" @click.prevent="openEditor('CREATE_TABLE_MAIN')"
                        class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                        create_table_main.sql</button>
                        corresponding to main stream to be enriched.<br />
                        Lookup Table
                        <button aria-label="button" @click.prevent="openEditor('CREATE_TABLE_LOOKUP')"
                        class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                        create_table_lookup.sql</button>
                        corresponding to lookup stream , containing the lookup values to be used for enrichment.<br />
                    </p>
                    <p class="text-gray-600">
                        The enrich sql is the main logic of this transform
                        <button aria-label="button bg-gray-50" @click.prevent="openEditor('ENRICH_SQL')"
                        class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-900 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                            enrich.sql</button>.
                    </p>

                    <!-- Inline Monaco Editor -->
                    <div
                        v-if="
                            editorVisible &&
                            ['CREATE_TABLE_MAIN', 'CREATE_TABLE_LOOKUP', 'ENRICH_SQL'].includes(
                                editorPath
                            )
                        "
                        class="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                        <div class="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                            <h3 class="text-lg font-medium">{{ editorPath }}</h3>
                            <button @click="closeEditor" aria-label="Close" class="text-gray-500 hover:text-gray-800 focus:outline-none">
                                <XMarkIcon class="w-5 h-5" />
                            </button>
                        </div>
                        <AppLoader v-if="editorLoading" :force-loading="editorLoading" class="h-168" />
                        <VueMonacoEditor
                            v-else
                            v-model:value="editorContent"
                            :language="editorLanguage"
                            :path="editorPath"
                            theme="vs"
                            class="h-168"
                            style="height: undefined !important; width: auto !important"
                            @change="onEditorChange"
                            @mount="onEditorMount" />
                    </div>
                </div>
            </section>

            <!--################################################################################################ -->
            <!--################################################################################################ -->
            <!--# Join ######################################################################################### -->
            <!--################################################################################################ -->
            <!--################################################################################################ -->

            <section v-if="transform?.transform_type == TRANSFORM_TYPE_ENUMS.sql_join" class="bg-white shadow rounded-lg p-6 transition-opacity">
                <h2 class="text-xl font-semibold mb-4"><span class="font-semibold mr-2">3.</span>Transform Logic</h2>
                <div class="bg-gray-50 border-l-4 border-gray-200 p-4 mb-4 space-y-4">
                    <p class="text-gray-600">
                        <code>CREATE TABLE</code> definitions, only fields used in the join are needed, the rest can be passed through automatically..
                    </p>
                    <p class="text-gray-600">
                        <div v-for="(pattern, idx) in inputPatterns" :key="idx">
                            Table {{ idx + 1 }}
                            <button
                                aria-label="button"
                                @click.prevent="openEditor(`CREATE_TABLE_${idx + 1}`)"
                                class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm"
                            >
                                create_table_{{ idx + 1 }}.sql
                            </button>
                            <span v-if="idx >= 2"> (Optional)</span>
                            corresponding to stream {{ idx + 1 }} to be joined topic pattern.<br />
                        </div>
                    </p>
                    <p class="text-gray-600">
                        The JOIN sql is the main logic of this transform
                        <button aria-label="button bg-gray-50" @click.prevent="openEditor('JOIN_SQL')"
                        class="text-sm cursor-pointer inline-flex items-center px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-900 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                            sql_join.sql</button>.
                    </p>

                    <!-- Inline Monaco Editor -->
                    <div
                        v-if="
                            editorVisible &&
                            ['CREATE_TABLE_1', 'CREATE_TABLE_2', 'CREATE_TABLE_3', 'CREATE_TABLE_4', 'CREATE_TABLE_5', 'JOIN_SQL'].includes(
                                editorPath
                            )
                        "
                        class="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                        <div class="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                            <h3 class="text-lg font-medium">{{ editorPath }}</h3>
                            <button @click="closeEditor" aria-label="Close" class="text-gray-500 hover:text-gray-800 focus:outline-none">
                                <XMarkIcon class="w-5 h-5" />
                            </button>
                        </div>
                        <AppLoader v-if="editorLoading" :force-loading="editorLoading" class="h-168" />
                        <VueMonacoEditor
                            v-else
                            v-model:value="editorContent"
                            :language="editorLanguage"
                            :path="editorPath"
                            theme="vs"
                            class="h-168"
                            style="height: undefined !important; width: auto !important"
                            @change="onEditorChange"
                            @mount="onEditorMount" />
                    </div>
                </div>
            </section>

            <!--# Map Filter ############################################################################################### -->

            <!-- 4. Deploy & Check -->
            <section class="bg-white shadow rounded-lg p-6 transition-opacity">
                <h2 class="text-xl font-semibold mb-4"><span class="font-semibold mr-2">4.</span>Deploy &amp; Check</h2>
                <ol class="space-y-6">
                    <li class="flex items-start space-x-4">
                        <span class="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 bg-gray-800 text-white rounded-full">4.0</span>
                        <div class="flex-grow">
                            <div class="flex items-center w-full">
                                <div class="w-full">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Output Topic pattern to clear</label>
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div class="flex items-center space-x-2">
                                                <input
                                                    v-model="outputTopicPatternToClear"
                                                    type="text"
                                                    placeholder=".*"
                                                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-cyan-200 focus:ring-opacity-50" />
                                                <button
                                                    @click="callClearOutputTopicsForTransform"
                                                    :disabled="implementationCurrentVersion?.version_id == null || loadingClearOutputTopics"
                                                    class="w-full inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-cyan-950 hover:bg-cyan-900 text-white focus:outline-none focus:ring transition disabled:opacity-50">
                                                    <ArrowPathIcon v-if="loadingClearOutputTopics" class="h-5 w-5 animate-spin mr-2" />
                                                    <CogIcon class="w-5 h-5 mr-2" />
                                                    Clear Output Topics and Job State
                                                </button>
                                            </div>
                                            <div class="rounded-md bg-yellow-50 p-4 flex items-start space-x-2 break-words w-full">
                                                <ExclamationTriangleIcon class="w-5 h-5 text-yellow-400" aria-hidden="true" />
                                                <p class="ml-3 text-sm text-yellow-700 whitespace-normal">
                                                    WARN: this will clear all output topics for this transform, so you can start fresh.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div v-if="clearOutputTopicsResult" class="rounded-md bg-green-50 p-4 mt-4">
                                            <div class="flex">
                                                <div class="shrink-0">
                                                    <CheckCircleIcon class="w-5 h-5 text-green-400" aria-hidden="true" />
                                                </div>
                                                <p class="ml-3 text-sm text-green-700">{{ clearOutputTopicsResult }}</p>
                                            </div>
                                        </div>
                                        <div v-if="clearOutputTopicsError" class="mt-2">
                                            <ErrorMessage :error="clearOutputTopicsError" :to-sentry="true" context="transform-impl-deploy-live" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                    <li class="flex items-start space-x-4">
                        <span class="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 bg-gray-800 text-white rounded-full">4.1</span>
                        <div class="flex-grow">
                            <div>
                                <label class="block text-sm font-medium text-gray-700">
                                    Replay Window (e.g. 3h, 2d, 10m). Use 0 to stream events from the last message processed by the previous transform version.
                                </label>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                                    <div class="flex items-center space-x-2">
                                        <input
                                            v-model="replayWindow"
                                            type="text"
                                            placeholder="24h"
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-cyan-200 focus:ring-opacity-50" />
                                        <button
                                            @click="callTriggerDeployLive"
                                            :disabled="implementationCurrentVersion?.version_id == null || loadingTriggerDeployLive"
                                            class="w-full inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-cyan-950 hover:bg-cyan-900 text-white focus:outline-none focus:ring transition disabled:opacity-50">
                                            <ArrowPathIcon v-if="loadingTriggerDeployLive" class="h-5 w-5 animate-spin mr-2" />
                                            <CogIcon class="w-5 h-5 mr-2" />
                                            Deploy Transform
                                        </button>
                                    </div>
                                    <div v-if="deployLiveResult" class="rounded-md bg-green-50 p-4 flex items-start space-x-2 break-words w-full">
                                        <CheckCircleIcon class="w-5 h-5 text-green-400" aria-hidden="true" />
                                        <p class="ml-3 text-sm text-green-700">{{ deployLiveResult }}</p>
                                    </div>
                                </div>
                                <div v-if="deployError" class="mt-2">
                                    <ErrorMessage :error="deployError" :to-sentry="true" context="transform-impl-deploy-live" />
                                </div>
                            </div>
                        </div>
                    </li>
                    <li class="flex items-start space-x-4">
                        <span class="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 bg-gray-800 text-white rounded-full">4.2</span>
                        <div class="flex-grow">
                            <div class="flex flex-col items-start space-y-4">
                                <button
                                    @click="callCheckJobStatus"
                                    :disabled="implementationCurrentVersion?.version_id == null || loadingCheckJobStatus"
                                    class="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-cyan-950 hover:bg-cyan-900 text-white focus:outline-none focus:ring transition disabled:opacity-50 w-[15rem]">
                                    <ArrowPathIcon v-if="loadingCheckJobStatus" class="h-5 w-5 animate-spin mr-2" />
                                    <CogIcon class="w-5 h-5 mr-2" />
                                    Check Job Status
                                </button>
                                <div v-if="jobStatusError">
                                    <ErrorMessage :error="jobStatusError" :to-sentry="true" context="transform-impl-check-job-status" />
                                </div>
                                <div v-else-if="jobStatus" class="mt-2">
                                    <div v-if="jobStatus.includes('RUNNING')" class="rounded-md bg-green-50 p-4 flex items-start space-x-2">
                                        <CheckCircleIcon class="w-5 h-5 text-green-400" aria-hidden="true" />
                                        <p class="ml-3 text-sm text-green-700">{{ jobStatus }}</p>
                                    </div>
                                    <div v-else class="rounded-md bg-yellow-50 p-4 flex items-start space-x-2">
                                        <ExclamationTriangleIcon class="w-5 h-5 text-yellow-400" aria-hidden="true" />
                                        <p class="ml-3 text-sm text-yellow-700">{{ jobStatus }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                    <li class="flex items-start space-x-4">
                        <span class="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 bg-gray-800 text-white rounded-full">4.3</span>
                        <div class="flex-grow">
                            <div class="flex flex-col items-start space-y-4">
                                <button
                                    @click="callCheckOutputTopicsForTransform"
                                    :disabled="implementationCurrentVersion?.version_id == null || loadingCheckInOutTopics"
                                    class="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-cyan-950 hover:bg-cyan-900 text-white focus:outline-none focus:ring transition disabled:opacity-50 w-[15rem]">
                                    <ArrowPathIcon v-if="loadingCheckInOutTopics" class="h-5 w-5 animate-spin mr-2" />
                                    <CogIcon class="w-5 h-5 mr-2" />
                                    Check Output Topics
                                </button>
                                <div v-if="hasCheckedOutputTopics" class="w-full">
                                    <div v-if="checkOutputTopicsError" class="mt-2">
                                        <ErrorMessage
                                            :error="checkOutputTopicsError"
                                            :to-sentry="true"
                                            context="transform-impl-check-output-topics" />
                                    </div>
                                    <div class="table-page-container">
                                        <div class="table-page-body">
                                            <div>
                                                <!-- TODO: transforms/topics-for-pattern API does not support pagination -->
                                                <div class="overflow-x-auto overflow-y-auto max-h-96 bg-white rounded-lg shadow-md">
                                                    <section class="paginated-section">
                                                        <AppLoader
                                                            :force-loading="loadingCheckInOutTopics"
                                                            default-height="30vh"
                                                            background="#f9fafb"
                                                            :default-width="'100%'"
                                                            :default-class="'shadow-md rounded-md'">
                                                            <table class="min-w-full divide-y divide-gray-300" aria-label="topics table">
                                                                <thead class="bg-gray-50">
                                                                    <tr>
                                                                        <th
                                                                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                            <div class="flex items-center gap-3">
                                                                                <span>Topic</span>
                                                                            </div>
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody class="bg-white divide-y divide-gray-200">
                                                                    <tr
                                                                        v-for="outTopic in checkOutputTopics"
                                                                        :key="outTopic"
                                                                        class="duration-300 ease-in-out hover:bg-gray-50">
                                                                        <td
                                                                            class="px-6 py-4 whitespace-nowrap cursor-pointer font-medium text-green-500 no-underline">
                                                                            <div class="flex flex-col items-start gap-1">
                                                                                <div
                                                                                    class="my-auto whitespace-normal break-words hover:text-green-500">
                                                                                    <a :href="`/topics/${outTopic}`" target="_blank">
                                                                                        {{ outTopic.replace(/(source_|transform_)[^.]+\\./, '') }}
                                                                                    </a>
                                                                                </div>
                                                                                <small class="font-regular text-gray-400">ID: {{ outTopic }}</small>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                    <tr v-if="!checkOutputTopics.length">
                                                                        <td class="px-6 py-4 text-gray-600 text-center">
                                                                            No output topics found for this transform.
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </AppLoader>
                                                    </section>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                    <li class="flex items-start space-x-4">
                        <span class="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 bg-gray-800 text-white rounded-full">4.4</span>
                        <div class="flex-grow">
                            <div class="flex flex-col items-start space-y-4">
                                <button
                                    @click="callCheckErrorsForTransform"
                                    :disabled="implementationCurrentVersion?.version_id == null || loadingCheckErrors"
                                    class="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-cyan-950 hover:bg-cyan-900 text-white focus:outline-none focus:ring transition disabled:opacity-50 w-[15rem]">
                                    <ArrowPathIcon v-if="loadingCheckErrors" class="h-5 w-5 animate-spin mr-2" />
                                    <CogIcon class="w-5 h-5 mr-2" />
                                    Check for Errors
                                </button>
                                <template v-if="!loadingCheckErrors && hasCheckedErrors && checkErrors.length > 0">
                                    <ErrorMessage v-for="err in checkErrors" :key="err.message" :error="err.message + '\n' + err.stacktrace" />
                                </template>
                                <div
                                    v-else-if="!loadingCheckErrors && hasCheckedErrors"
                                    class="rounded-md bg-green-50 p-4 flex items-start space-x-2">
                                    <CheckCircleIcon class="w-5 h-5 text-green-400" aria-hidden="true" />
                                    <p class="ml-3 text-sm text-green-700">No errors.</p>
                                </div>
                            </div>
                        </div>
                    </li>
                </ol>
            </section>
        </div>
    </AppLoader>
</template>

<script setup>
import { TRANSFORM_TYPE_ENUMS, TRANSFORM_LANGUAGE_ENUM } from '../../utils/constants';

import { ref, computed, onMounted, watch } from 'vue';
import _ from 'lodash';
import { VueMonacoEditor } from '@guolao/vue-monaco-editor';
import { debounce } from 'lodash';
import AppLoader from '@/components/AppLoader.vue';

import { CogIcon, XMarkIcon, ArrowPathIcon, PlusIcon } from '@heroicons/vue/24/solid';
import DropdownFilters from '@/components/DropdownFilters.vue';
import Pagination from '@/components/Pagination.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';
import { useTopicsStore } from '@/stores/topics.store';
import { useLoaderStore } from '@/stores/loader.store';
import { FILTER_CONSTANTS, formatter, getMetricFormattingInfo } from '@/utils/utils';
import { CodeBracketIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { ExclamationTriangleIcon, CheckCircleIcon, CheckIcon } from '@heroicons/vue/20/solid';
import { useRouter } from 'vue-router';
import { getConnectorIcon } from '@/utils/constants';
import { useTransformsStore } from '@/stores/transforms.store';

const transformsStore = useTransformsStore();
const loadingPage = ref(false);

// Editor state
const editorLoading = ref(false);
const editorVisible = ref(false);
const editorContent = ref('');
const editorLanguage = ref('javascript');
const editorPath = ref('');
// Editor save state
let saveDebounced;
const hasUnsavedEditorChanges = ref(false);
const savingEditor = ref(false);

// Reactive form & results
const transformObjective = ref('');
const pattern = ref('');
const inputPatterns = ref([]); // Reactive list for input patterns
const patternMainTable = ref('');
const patternLookupTable = ref('');
const outputTopic = ref('');
const setInOutResult = ref('');
const setInOutError = ref(null);
const checkOutputTopics = ref([]);
const fetchInOutError = ref(null);
const checkErrors = ref([]);
const replayWindow = ref('');
const clearOutputTopicsResult = ref('');
const clearOutputTopicsError = ref(null);
const deployLiveResult = ref('');
const deployError = ref(null);
const jobStatus = ref(false);
const jobStatusError = ref(null);
const checkOutputTopicsError = ref(null);
const hasCheckedOutputTopics = ref(false);
const hasCheckedErrors = ref(false);

// fetched data
const transform = ref(null);
const implementationHistory = ref([]);
const implementationCurrentVersion = ref(null);
const inOutTopics = ref([]);
const outputTopicPatternToClear = ref('.*'); // Pattern to clear output topics

// Accordion state
const showPreview = ref(false);

// Loading & step flags
const loadingSetInOutTopics = ref(false);
const loadingFetchInOutTopics = ref(false);
const loadingCheckInOutTopics = ref(false);
const loadingTriggerDeployLive = ref(false);
const loadingCheckJobStatus = ref(false);
const loadingCheckErrors = ref(false);
const loadingClearOutputTopics = ref(false);
const clearOutputError = ref(null);

watch(
    () => implementationCurrentVersion.value?.implementation?.inputTables,
    (newInputTables) => {
        if (newInputTables) {
            inputPatterns.value = newInputTables.map((table) => table.topicMatcherRegex || '');
        }
    },
    { immediate: true }
);

// Add a new pattern
function addInputPattern() {
    inputPatterns.value.push(''); // Add an empty pattern
    implementationCurrentVersion.value.implementation.inputTables.push({
        topicMatcherRegex: '',
        createTableSQL: 'TBD',
        name: `input_table_${inputPatterns.value.length}`
    });
    computePatternForJoin();
}

// Remove a pattern
function removeInputPattern(index) {
    inputPatterns.value.splice(index, 1); // Remove the pattern from the list
    implementationCurrentVersion.value.implementation.inputTables.splice(index, 1); // Remove the corresponding input table
    computePatternForJoin();
}

// Update the pattern in inputTables when the user edits it
function updateInputPattern(index, value) {
    inputPatterns.value[index] = value;
    implementationCurrentVersion.value.implementation.inputTables[index].topicMatcherRegex = value;
    computePatternForJoin();
}

// Open file in Monaco editor
async function openEditor(name) {
    editorLoading.value = true;
    editorVisible.value = true;
    try {
        setEditorContent(name);
        editorPath.value = name;
        // Autosave after user stops typing
        saveDebounced = debounce(() => {
            saveCurrentImplementation();
            console.log('Auto-saving', name, implementationCurrentVersion.value);
        }, 2000);
        // Reset change state when opening editor
        hasUnsavedEditorChanges.value = false;
    } catch (e) {
        console.error('Failed to load file', name, e);
    } finally {
        editorLoading.value = false;
    }
}

function setEditorContent(name) {
    let transform_type = transform?.value.transform_type;
    console.log('setEditorContent', transform_type, name);
    if (
        transform_type === TRANSFORM_TYPE_ENUMS.map_filter ||
        transform_type === TRANSFORM_TYPE_ENUMS.enrich_async ||
        transform_type === TRANSFORM_TYPE_ENUMS.fan_out ||
        transform_type === TRANSFORM_TYPE_ENUMS.un_nesting
    ) {
        var ext = '.js';
        if (implementationCurrentVersion.value?.implementation.language?.toUpperCase() === TRANSFORM_LANGUAGE_ENUM.PYTHON) {
            ext = '.py';
        }
        if (name === 'VALUE_TRANSFORM') {
            editorContent.value = implementationCurrentVersion.value?.implementation.value_transform;
            editorLanguage.value = getEditorLanguage();
        } else if (name === 'KEY_TRANSFORM') {
            editorContent.value = implementationCurrentVersion.value?.implementation.key_transform;
            editorLanguage.value = getEditorLanguage();
        } else if (name === 'TOPIC_TRANSFORM') {
            editorContent.value = implementationCurrentVersion.value?.implementation.topic_transform;
            editorLanguage.value = getEditorLanguage();
        }
    } else if (transform_type === TRANSFORM_TYPE_ENUMS.sql_join) {
        if (name === 'JOIN_SQL') {
            editorContent.value = implementationCurrentVersion.value?.implementation.joinSQL;
            editorLanguage.value = 'sql';
        } else if (name == 'CREATE_TABLE_1') {
            editorContent.value = implementationCurrentVersion.value?.implementation?.inputTables[0].createTableSQL;
            editorLanguage.value = 'sql';
        } else if (name == 'CREATE_TABLE_2') {
            editorContent.value = implementationCurrentVersion.value?.implementation?.inputTables[1].createTableSQL;
            editorLanguage.value = 'sql';
        } else if (name == 'CREATE_TABLE_3') {
            editorContent.value = implementationCurrentVersion.value?.implementation?.inputTables[2].createTableSQL;
            editorLanguage.value = 'sql';
        } else if (name == 'CREATE_TABLE_4') {
            editorContent.value = implementationCurrentVersion.value?.implementation?.inputTables[3].createTableSQL;
            editorLanguage.value = 'sql';
        } else if (name == 'CREATE_TABLE_5') {
            editorContent.value = implementationCurrentVersion.value?.implementation?.inputTables[4].createTableSQL;
            editorLanguage.value = 'sql';
        }
    } else if (transform_type === TRANSFORM_TYPE_ENUMS.enrich) {
        if (name === 'ENRICH_SQL') {
            editorContent.value = implementationCurrentVersion.value?.implementation.enrichSQL;
            editorLanguage.value = 'sql';
        } else if (name == 'CREATE_TABLE_MAIN') {
            editorContent.value = implementationCurrentVersion.value?.implementation?.mainTable.createTableSQL;
            editorLanguage.value = 'sql';
        } else if (name == 'CREATE_TABLE_LOOKUP') {
            editorContent.value = implementationCurrentVersion.value?.implementation?.lookupTable.createTableSQL;
            editorLanguage.value = 'sql';
        }
    }
}

function readEditorContent(suppliedEditorContent) {
    // Guard against missing implementation (e.g., before load)
    if (!implementationCurrentVersion.value?.implementation) {
        console.warn('readEditorContent: no implementationCurrentVersion available to update');
        return;
    }
    var transform_type = transform?.value.transform_type;
    var name = editorPath.value;
    console.log('getEditorContent', transform_type, name);
    if (
        transform_type === TRANSFORM_TYPE_ENUMS.map_filter ||
        transform_type === TRANSFORM_TYPE_ENUMS.enrich_async ||
        transform_type === TRANSFORM_TYPE_ENUMS.fan_out ||
        transform_type === TRANSFORM_TYPE_ENUMS.un_nesting
    ) {
        if (name === 'VALUE_TRANSFORM') {
            implementationCurrentVersion.value.implementation.value_transform = suppliedEditorContent;
        } else if (name === 'KEY_TRANSFORM') {
            implementationCurrentVersion.value.implementation.key_transform = suppliedEditorContent;
        } else if (name === 'TOPIC_TRANSFORM') {
            implementationCurrentVersion.value.implementation.topic_transform = suppliedEditorContent;
        }
    } else if (transform_type === TRANSFORM_TYPE_ENUMS.sql_join) {
        if (name === 'JOIN_SQL') {
            implementationCurrentVersion.value.implementation.joinSQL = suppliedEditorContent;
        } else if (name === 'CREATE_TABLE_1') {
            implementationCurrentVersion.value.implementation.inputTables[0].createTableSQL = suppliedEditorContent;
            implementationCurrentVersion.value.implementation.inputTables[0].topicMatcherRegex = inputPatterns.value[0];
        } else if (name === 'CREATE_TABLE_2') {
            implementationCurrentVersion.value.implementation.inputTables[1].createTableSQL = suppliedEditorContent;
            implementationCurrentVersion.value.implementation.inputTables[1].topicMatcherRegex = inputPatterns.value[1];
        } else if (name === 'CREATE_TABLE_3') {
            implementationCurrentVersion.value.implementation.inputTables[2].createTableSQL = suppliedEditorContent;
            implementationCurrentVersion.value.implementation.inputTables[2].topicMatcherRegex = inputPatterns.value[2];
        } else if (name === 'CREATE_TABLE_4') {
            implementationCurrentVersion.value.implementation.inputTables[3].createTableSQL = suppliedEditorContent;
            implementationCurrentVersion.value.implementation.inputTables[3].topicMatcherRegex = inputPatterns.value[3];
        } else if (name === 'CREATE_TABLE_5') {
            implementationCurrentVersion.value.implementation.inputTables[4].createTableSQL = suppliedEditorContent;
            implementationCurrentVersion.value.implementation.inputTables[4].topicMatcherRegex = inputPatterns.value[4];
        }
    } else if (transform_type === TRANSFORM_TYPE_ENUMS.enrich) {
        if (name === 'ENRICH_SQL') {
            implementationCurrentVersion.value.implementation.enrichSQL = suppliedEditorContent;
        } else if (name === 'CREATE_TABLE_MAIN') {
            implementationCurrentVersion.value.implementation.mainTable.createTableSQL = suppliedEditorContent;
            implementationCurrentVersion.value.implementation.mainTable.topicMatcherRegex = patternMainTable.value;
        } else if (name === 'CREATE_TABLE_LOOKUP') {
            implementationCurrentVersion.value.implementation.lookupTable.createTableSQL = suppliedEditorContent;
            implementationCurrentVersion.value.implementation.lookupTable.topicMatcherRegex = patternLookupTable.value;
        }
    }
}

async function saveCurrentImplementation() {
    savingEditor.value = true;
    try {
        const savedImplDetails = await transformsStore.editImplementationDetails(
            transform.value.id,
            implementationCurrentVersion.value
        );
        console.log('Saved implementation details', savedImplDetails);
        implementationCurrentVersion.value = savedImplDetails;
        hasUnsavedEditorChanges.value = false;
    } finally {
        savingEditor.value = false;
    }
}

function onEditorMount(editor, monaco) {
    console.log('Monaco mounted for', editorPath.value);
    // Intercept Ctrl+S / Cmd+S to trigger save in editor and prevent browser save
    editor.onKeyDown((e) => {
        if (e.keyCode === monaco.KeyCode.KeyS && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            saveCurrentImplementation();
        }
    });
}

function onEditorChange(val) {
    readEditorContent(val);
    // flag unsaved changes & trigger autosave
    hasUnsavedEditorChanges.value = true;
    saveDebounced && saveDebounced();
}

function closeEditor() {
    editorVisible.value = false;
}

// Mock API calls with 2s delay
const callSetInOutTopics = async () => {
    // Validate input regex pattern before sending backend request
    try {
        new RegExp(pattern.value);
    } catch (err) {
        setInOutError.value = `Invalid regex topic pattern: ${pattern.value}`;
        return;
    }
    loadingSetInOutTopics.value = true;
    setInOutResult.value = '';
    setInOutError.value = null;
    try {
        await transformsStore.setInputOutputTopic(transform.value.id, pattern.value, outputTopic.value);
        var transform_type = transform?.value?.transform_type;
        if (transform_type === TRANSFORM_TYPE_ENUMS.sql_join) {
            var inputTables = implementationCurrentVersion.value.implementation.inputTables;
            for (let i = 0; i < inputTables.length; i++) {
                if (inputPatterns.value[i]) {
                    inputTables[i].topicMatcherRegex = inputPatterns.value[i];
                } else {
                    // If the pattern is empty, we can remove the input table
                    inputTables.splice(i, 1);
                    i--; // Adjust index after removal
                }
            }
            await saveCurrentImplementation();
        } else if (transform_type === TRANSFORM_TYPE_ENUMS.enrich) {
            implementationCurrentVersion.value.implementation.mainTable.topicMatcherRegex = patternMainTable?.value;
            implementationCurrentVersion.value.implementation.lookupTable.topicMatcherRegex = patternLookupTable?.value;
            await saveCurrentImplementation();
        }
        setInOutResult.value = 'Successfully set input/output topics';
    } catch (e) {
        console.error('Error setting input/output topics', e);
        // Display error via ErrorMessage rather than green success box
        setInOutResult.value = '';
        setInOutError.value = e;
    } finally {
        loadingSetInOutTopics.value = false;
    }
};

const callTriggerDeployLive = async () => {
    loadingTriggerDeployLive.value = true;
    deployError.value = null;
    deployLiveResult.value = '';

    if (inOutTopics.value.length === 0) {
        deployError.value = 'No input topics match the existing pattern. Please choose the input topics first.';
        loadingTriggerDeployLive.value = false;
        return;
    }

    const transformId = transform.value.id;
    const versionId = implementationCurrentVersion.value.version_id;
    const startTime = Date.now();
    try {
        await transformsStore.deployPreview(transformId, NO_VERSION, replayWindow.value);
        const resLive = await transformsStore.deployLive(transformId, NO_VERSION);
        console.log('deploy_live_result', resLive);

        let endTime = new Date().getTime();
        let durationSec = Math.floor((endTime - startTime) / 1000);
        deployLiveResult.value = `Deployed Successfully in ${durationSec} sec.`;

        await callCheckJobStatus();
        await callCheckOutputTopicsForTransform();
        await callCheckErrorsForTransform();
    } catch (e) {
        console.error('Error during deploy live', e);
        deployError.value = e;
    } finally {
        loadingTriggerDeployLive.value = false;
    }
    loadingTriggerDeployLive.value = false;
};

const callCheckJobStatus = async () => {
    loadingCheckJobStatus.value = true;
    jobStatusError.value = null;
    jobStatus.value = '';
    try {
        const res = await transformsStore.getJobStatus(transform.value.id);
        jobStatus.value = `Job is ${res.status}, job id ${res.id}, job name ${res.job_name}, start time ${res.start_time}`;
    } catch (e) {
        console.error('Error checking job status', e);
        jobStatusError.value = e;
    } finally {
        loadingCheckJobStatus.value = false;
    }
};

const callCheckOutputTopicsForTransform = async () => {
    hasCheckedOutputTopics.value = true;
    loadingCheckInOutTopics.value = true;
    checkOutputTopicsError.value = null;
    checkOutputTopics.value = [];
    try {
        const res = await transformsStore.getOutputTopicsForTransform(transform.value.id);
        if (!res || res.length === 0) {
            checkOutputTopics.value = [];
        } else {
            checkOutputTopics.value = res;
        }
    } catch (e) {
        console.error('Error checking output topics', e);
        checkOutputTopicsError.value = e;
    } finally {
        loadingCheckInOutTopics.value = false;
    }
};

const callCheckErrorsForTransform = async () => {
    hasCheckedErrors.value = true;
    loadingCheckErrors.value = true;
    checkErrors.value = [];
    try {
        console.log('Checking errors for transform', transform.value.id);
        const res = await transformsStore.getErrorsForTransform(transform.value.id);
        checkErrors.value = res;
    } catch (e) {
        console.error('Error checking errors for transform', e);
        checkErrors.value = [
            {
                message: 'Error checking errors for transform',
                stacktrace: `Error: ${e.response.data.detail.replace(/\\n/g, '<br/>')}`
            }
        ];
    } finally {
        loadingCheckErrors.value = false;
    }
};

// Topics table search & pagination
const topicsStore = useTopicsStore();
const loaderStore = useLoaderStore();
const router = useRouter();
const page = ref(0);
const pageSize = ref(10);
const topicTableDataLoading = ref(false);
const checkedFilters = ref([]);
const filters = ref([
    {
        title: 'Topics per page',
        name: 'count',
        defaultIndex: 0,
        options: [
            { value: 10, displayValue: '10/page' },
            { value: 20, displayValue: '20/page' },
            { value: 50, displayValue: '50/page' }
        ]
    }
]);
const topicTableMetricsLoading = computed(() => loaderStore.isLoading('metrics.topics.fetch.table'));

const props = defineProps({
    transformId: {
        type: String,
        required: true
    }
});

async function loadTransform() {
    loadingPage.value = true;
    try {
        transform.value = await transformsStore.getById(props.transformId);
        pattern.value = transform.value.config['transforms.input.topic.pattern'] || '';
        outputTopic.value = transform.value.config['transforms.output.topic.pattern'] || '';
        const implementation_versions_res = await transformsStore.getImplementationDetails(props.transformId);
        const implementation_versions = implementation_versions_res.impl_versions;
        implementationHistory.value = implementation_versions || [];
        const latest_version = Object.keys(implementation_versions).sort().reverse()[0];
        implementationCurrentVersion.value = implementation_versions[latest_version] || null;
        console.log('Loaded transform', transform.value.transform_type, transform.value);
        console.log('Loaded transform implementation', implementationCurrentVersion.value);
        if (transform.value.transform_type === TRANSFORM_TYPE_ENUMS.sql_join) {
            if (implementationCurrentVersion.value?.implementation?.inputTables?.length > 0) {
                inputPatterns.value[0] = implementationCurrentVersion.value.implementation.inputTables[0].topicMatcherRegex || '';
            }
            if (implementationCurrentVersion.value?.implementation?.inputTables?.length > 1) {
                inputPatterns.value[1] = implementationCurrentVersion.value.implementation.inputTables[1].topicMatcherRegex || '';
            }
            if (implementationCurrentVersion.value?.implementation?.inputTables?.length > 2) {
                inputPatterns.value[2] = implementationCurrentVersion.value.implementation.inputTables[2].topicMatcherRegex || '';
            }
            if (implementationCurrentVersion.value?.implementation?.inputTables?.length > 3) {
                inputPatterns.value[3] = implementationCurrentVersion.value.implementation.inputTables[3].topicMatcherRegex || '';
            }
            if (implementationCurrentVersion.value?.implementation?.inputTables?.length > 4) {
                inputPatterns.value[4] = implementationCurrentVersion.value.implementation.inputTables[4].topicMatcherRegex || '';
            }
        } else if (transform.value.transform_type === TRANSFORM_TYPE_ENUMS.enrich) {
            console.log('loading main and lookup inputTables', implementationCurrentVersion.value?.implementation?.inputTables);
            patternMainTable.value = implementationCurrentVersion.value.implementation.mainTable?.topicMatcherRegex || '';
            patternLookupTable.value = implementationCurrentVersion.value.implementation.lookupTable?.topicMatcherRegex || '';
        }
    } finally {
        loadingPage.value = false;
    }
}

onMounted(() => {
    loadTransform();
    initializeTopicsFilters();
    // clear initial topics list until user searches via pattern input
    topicsStore.topics = [];
});

// search topics based on pattern input with debounce
watch(
    pattern,
    debounce(() => {
        page.value = 0;
        // fetchTopicsTableData();
        fetchInOutTopics();
    }, 1000)
);
watch(
    outputTopic,
    debounce(() => {
        page.value = 0;
        // fetchTopicsTableData();
        fetchInOutTopics();
    }, 1000)
);

watch(inputPatterns,debounce(() => {computePatternForJoin()}, 100));

watch(patternMainTable,debounce(() => {computePatternForEnrich()}, 100));
watch(patternLookupTable,debounce(() => {computePatternForEnrich()}, 100));

function computePatternForJoin() {
    if (transform.value.transform_type === TRANSFORM_TYPE_ENUMS.sql_join) {
        pattern.value = "(" + inputPatterns?.value.join(')|(') + ")";
    }
    return pattern.value;
}

function computePatternForEnrich() {
    if (transform.value.transform_type === TRANSFORM_TYPE_ENUMS.enrich) {
        pattern.value = "(" + patternMainTable.value + ")|(" + patternLookupTable.value + ")";
    }
    return pattern.value;
}

function initializeTopicsFilters() {
    filters.value.forEach((filter) => {
        if (filter.name === 'count') {
            pageSize.value = filter.options[filter.defaultIndex].value;
        }
        checkedFilters.value[filter.name] = [filter.multiple ? FILTER_CONSTANTS.all : filter.options[filter.defaultIndex]];
    });
}

const NO_VERSION = '0';
/**
 * @returns Array<Pair<string, string>> - Array of input/output topic pairs
 */
async function fetchInOutTopics() {
    // Validate input regex before querying backend
    try {
        new RegExp(pattern.value);
    } catch (err) {
        fetchInOutError.value = `Invalid regex topic pattern: ${pattern.value}`;
        inOutTopics.value = [];
        return;
    }
    topicTableDataLoading.value = true;
    fetchInOutError.value = null;
    try {
        const res = await transformsStore.topicsForPattern(pattern.value, outputTopic.value);
        console.log('in_out_topics', res);
        var ret = 'Topics:\n';
        var inOutTopicsRes = [];
        var outputTopicPrefix = `transform_${transform.value.id}_${NO_VERSION}`;
        var idx = 0;
        try {
            for (var in_out_topic of res) {
                idx++;
                ret += `${in_out_topic[0]} --> ${in_out_topic[1]}\n`;
                inOutTopicsRes.push([in_out_topic[0], `${outputTopicPrefix}.${in_out_topic[1]}`]);
                if (idx > 20) {
                    break;
                }
            }
            console.log('in_out_topics', ret);
            inOutTopics.value = inOutTopicsRes;
            return res;
        } catch (e) {
            console.error('Error processing in_out_topics', e);
        }
    } catch (e) {
        console.error('Error fetching topics for pattern', e);
        fetchInOutError.value = e;
    } finally {
        topicTableDataLoading.value = false;
    }
}

async function callClearOutputTopicsForTransform() {
    loadingClearOutputTopics.value = true;
    try {
        var startTime = new Date().getTime();
        await transformsStore.clearOutputTopics(transform.value.id, outputTopicPatternToClear.value);
        var endTime = new Date().getTime();
        if (!replayWindow.value) {
            // replayWindow needs to be defined, otherwise the previous consumer group offsets will be used and Clear topics will have not effect
            replayWindow.value = '7d';
        }
        clearOutputTopicsResult.value = `Successfully cleared output topics matching pattern: ${outputTopicPatternToClear.value} in ${Math.floor((endTime - startTime) / 1000)} sec.`;
    } catch (e) {
        console.error('Error clearing output topics', e);
        clearOutputTopicsError.value = e;
    } finally {
        loadingClearOutputTopics.value = false;
    }
}

async function fetchTopicsTableData(newPage = null, newPageSize = null) {
    topicTableDataLoading.value = true;
    try {
        page.value = _.isNil(newPage) ? page.value : newPage;
        pageSize.value = _.isNil(newPageSize) ? pageSize.value : newPageSize;

        if (!pattern.value) {
            topicsStore.topics = [];
            return;
        }

        await topicsStore.fetch({
            page_size: pageSize.value,
            page_number: page.value,
            partial_name: pattern.value
        });

        return topicsStore.all;
    } catch (error) {
        console.error(error);
    } finally {
        topicTableDataLoading.value = false;
    }
}

function onTopicsFilterToggle({ filter, filterOption }) {
    try {
        let newPageSize = topicsStore.pageSize;
        if (filter.name === 'count') {
            newPageSize = filterOption.value;
        }
        page.value = 0;
        fetchTopicsTableData(page.value, newPageSize);
    } catch (error) {
        console.error(error);
    }
}

const paginationMessage = computed(() => ({
    show: !!topicsStore.total,
    firstPart: 'Showing topics',
    middlePart: 'to',
    lastPart: `of ${topicsStore.total}`
}));

function getFormattedValue(metricKey, value) {
    const metricInfo = getMetricFormattingInfo(metricKey, topicsStore.configs?.metrics);
    return formatter(value, metricInfo || {});
}

function selectedConnectorRouterPath(id, type) {
    return type?.includes('source') ? { name: 'source-details', params: { id } } : { name: 'destination-details', params: { id } };
}

const goToTopic = (topic) => {
    router.push({ name: 'topic-details', params: { topic_id: topic.id } });
};

function getFileExtension() {
    const lang = implementationCurrentVersion.value?.implementation.language;
    return lang?.toUpperCase() === TRANSFORM_LANGUAGE_ENUM.PYTHON ? '.py' : '.js';
}

function getEditorLanguage() {
    const lang = implementationCurrentVersion.value?.implementation.language;
    return lang?.toUpperCase() === TRANSFORM_LANGUAGE_ENUM.PYTHON ? 'python' : 'javascript';
}
</script>

<style scoped>
.editor-backdrop {
    background-color: rgba(0, 0, 0, 0.3);
}

:deep(.monaco-editor .monaco-editor-background) {
    background: rgba(255, 255, 255, 0.3) !important;
}
</style>
